import { useEffect, useState } from "react";
import { vmarketsAPI } from "@/services/api";
import { assetsAPI } from "@/services/api";
import {
  VirtualMarketResponseSchema,
  VirtualMarketCreateSchema,
  VirtualMarketUpdateSchema,
  MarketExchangeSchema,
} from "@/types/vmarkets";
import { Asset } from "@/types/assets";
import { Button } from "@/components/ui/shadcn/button";
import { Input } from "@/components/ui/shadcn/input";
import { Table } from "@/components/ui/shadcn/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/shadcn/dialog";
import { Card } from "@/components/ui/shadcn/card";
import { DialogDescription } from "@radix-ui/react-dialog";
import { Header } from "@/components/layout/Header";
import { useToast } from "@/hooks/use-toast";
import { Pencil, Trash, Plus } from "lucide-react";
import { handleApiError, showSuccessToast } from "@/lib/error-handler";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/shadcn/select";
import { Exchange } from "@/types/exchanges";

export default function VirtualMarketsDashboard() {
  const { toast } = useToast();
  const [vmarkets, setVmarkets] = useState<VirtualMarketResponseSchema[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [isDialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedVmarket, setSelectedVmarket] = useState<VirtualMarketResponseSchema | null>(null);
  const [newVmarket, setNewVmarket] = useState<Omit<VirtualMarketCreateSchema, 'hops'> & { hops: MarketExchangeSchema[] }>({
    description: "",
    base: "",
    counter: "",
    hops: [{ base: "", counter: "", exchange: Exchange.BINANCE }],
    priority: 0,
  });

  useEffect(() => {
    fetchVmarkets();
    fetchAssets();
  }, []);

  const fetchVmarkets = async () => {
    setLoading(true);
    try {
      const response = await vmarketsAPI.getVirtualMarkets(true);
      setVmarkets(response);
    } catch (error) {
      handleApiError("Fetch Virtual Markets", error, toast);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssets = async () => {
    try {
      const response = await assetsAPI.getAssets();
      setAssets(response);
    } catch (error) {
      handleApiError("Fetch Assets", error, toast);
    }
  };

  const handleCreateVmarket = async () => {
    if (!newVmarket.description || !newVmarket.base || !newVmarket.counter || newVmarket.hops.length === 0) {
      toast({
        title: "Validation Error",
        description: "Description, base, counter and at least one hop are required!",
        variant: "error",
      });
      return;
    }

    setLoading(true);
    try {
      const vmarketToCreate: VirtualMarketCreateSchema = {
        ...newVmarket,
      };
      const response = await vmarketsAPI.createVirtualMarket(vmarketToCreate);
      showSuccessToast(toast, "Success", "Virtual market created successfully!");
      setDialogOpen(false);
      fetchVmarkets();
      resetForm();
    } catch (error) {
      handleApiError("Create Virtual Market", error, toast);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateVmarket = async (id: number, updateData: Partial<VirtualMarketUpdateSchema>) => {
    setLoading(true);
    try {
      await vmarketsAPI.updateVirtualMarket(id, updateData);
      showSuccessToast(toast, "Success", "Virtual market updated successfully!");
      setDialogOpen(false);
      fetchVmarkets();
      resetForm();
    } catch (error) {
      handleApiError("Update Virtual Market", error, toast);
    } finally {
      setLoading(false);
    }
  };

  const handleEditVmarket = async () => {
    if (!selectedVmarket) return;

    setLoading(true);
    try {
      const updateData: VirtualMarketUpdateSchema = {
        description: selectedVmarket.description,
        hops: selectedVmarket.route.hops.map(hop => ({
          base: hop.base,
          counter: hop.counter,
          exchange: hop.exchange,
        })),
        priority: selectedVmarket.priority,
        is_enabled: selectedVmarket.is_enabled,
      };
      await vmarketsAPI.updateVirtualMarket(selectedVmarket.id, updateData);
      showSuccessToast(toast, "Success", "Virtual market updated successfully!");
      setDialogOpen(false);
      fetchVmarkets();
      resetForm();
    } catch (error) {
      handleApiError("Update Virtual Market", error, toast);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (id: number, isEnabled: boolean) => {
    await handleUpdateVmarket(id, { is_enabled: isEnabled });
  };

  const handleDeleteVmarket = async (id: number) => {
    if (!window.confirm("Are you sure you want to delete this virtual market?"))
      return;

    setLoading(true);
    try {
      await vmarketsAPI.deleteVirtualMarket(id);
      showSuccessToast(toast, "Success", "Virtual market deleted successfully!");
      fetchVmarkets();
    } catch (error) {
      handleApiError("Delete Virtual Market", error, toast);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setNewVmarket({
      description: "",
      base: "",
      counter: "",
      hops: [{ base: "", counter: "", exchange: Exchange.BINANCE }],
      priority: 0,
    });
    setSelectedVmarket(null);
  };

  const addHop = () => {
    setNewVmarket({
      ...newVmarket,
      hops: [
        ...newVmarket.hops,
        { base: "", counter: "", exchange: Exchange.BINANCE },
      ],
    });
  };

  const removeHop = (index: number) => {
    if (newVmarket.hops.length <= 1) {
      toast({
        title: "Validation Error",
        description: "A virtual market must have at least one hop",
        variant: "error",
      });
      return;
    }
    setNewVmarket({
      ...newVmarket,
      hops: newVmarket.hops.filter((_, i) => i !== index),
    });
  };

  const updateHop = (index: number, field: keyof MarketExchangeSchema, value: string) => {
    const updatedHops = [...newVmarket.hops];
    updatedHops[index] = {
      ...updatedHops[index],
      [field]: value,
    };
    setNewVmarket({
      ...newVmarket,
      hops: updatedHops,
    });
  };

  useEffect(() => {
    if (newVmarket.base && newVmarket.counter && newVmarket.hops.length > 0) {
      const updatedHops = [...newVmarket.hops];
      updatedHops[0] = {
        ...updatedHops[0],
        base: newVmarket.base,
        counter: newVmarket.counter,
      };
      setNewVmarket({
        ...newVmarket,
        hops: updatedHops,
      });
    }
  }, [newVmarket.base, newVmarket.counter]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="container py-6">
        <h1 className="text-2xl font-bold text-gray-800">
          Virtual Markets Management
        </h1>
        <Card className="p-6 shadow-lg rounded-xl">
          <div className="flex justify-between mb-4">
            <Button
              className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-800 hover:to-blue-900 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-200 flex items-center"
              onClick={() => {
                resetForm();
                setDialogOpen(true);
              }}
            >
              <Plus className="mr-2 h-5 w-5" /> Add Virtual Market
            </Button>
          </div>
          {loading ? (
            <p className="text-gray-500 text-center">Loading virtual markets...</p>
          ) : (
            <Table className="w-full border rounded-md">
              <thead className="bg-gray-100">
                <tr>
                  <th className="p-2 text-left">Base</th>
                  <th className="p-2 text-left">Counter</th>
                  <th className="p-2 text-left">Description</th>
                  <th className="p-2 text-left">Route</th>
                  {/* <th className="p-2 text-left">Priority</th> */}
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {vmarkets.map((vmarket) => (
                  <tr key={vmarket.id} className="border-t hover:bg-gray-50">
                    <td className="p-2">{vmarket.base}</td>
                    <td className="p-2">{vmarket.counter}</td>
                    <td className="p-2">{vmarket.description}</td>
                    <td className="p-2">
                      {vmarket.route.hops.map((hop, i) => (
                        <div key={i} className="text-sm">
                          {hop.base}/{hop.counter} @ {hop.exchange}
                          {hop.inverted ? " (inverted)" : ""}
                        </div>
                      ))}
                    </td>
                    {/* <td className="p-2">{vmarket.priority}</td> */}
                    <td className="p-2">
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${
                          vmarket.is_enabled
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {vmarket.is_enabled ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="p-2 flex gap-2">
                      <Button
                        variant="outline"
                        className="flex items-center"
                        onClick={() => {
                          setSelectedVmarket(vmarket);
                          setDialogOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4 mr-1" />
                        Edit
                      </Button>
                      <Button
                        className={`flex items-center ${vmarket.is_enabled ? "bg-red-500 hover:bg-red-600" : "bg-green-500 hover:bg-green-600"}`}
                        onClick={() => handleToggleEnabled(vmarket.id, !vmarket.is_enabled)}
                      >
                        {vmarket.is_enabled ? "Disable" : "Enable"}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card>

        {/* Virtual Market Dialog (Create & Edit) */}
        {isDialogOpen && (
          <Dialog open={isDialogOpen} onOpenChange={setDialogOpen}>
            <DialogContent className="sm:max-w-[600px] rounded-lg shadow-2xl">
              <DialogHeader>
                <DialogTitle className="text-lg font-bold text-gray-900 dark:text-white">
                  {selectedVmarket ? "Edit Virtual Market" : "Create Virtual Market"}
                </DialogTitle>
                <DialogDescription className="text-sm text-gray-500">
                  {selectedVmarket
                    ? "Update the virtual market details below."
                    : "Fill in the virtual market details below to create a new virtual market."}
                </DialogDescription>
                <div className="mt-2 p-3 bg-blue-50 rounded-md">
                  <p className="text-sm text-blue-700">
                    <strong>Tip:</strong> Use a description that explains your routing strategy. The base/counter pair (e.g., BTC/USDT) will be shown separately. For example:
                    <ul className="list-disc list-inside mt-1 ml-2">
                      <li>"Binance Direct"</li>
                      <li>"Binance via USDC"</li>
                      <li>"Kraken Direct"</li>
                    </ul>
                  </p>
                </div>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <Input
                    placeholder="Enter routing strategy description"
                    value={selectedVmarket ? selectedVmarket.description : newVmarket.description}
                    onChange={(e) =>
                      selectedVmarket
                        ? setSelectedVmarket({ ...selectedVmarket, description: e.target.value })
                        : setNewVmarket({ ...newVmarket, description: e.target.value })
                    }
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Base Asset
                    </label>
                    <Select
                      value={selectedVmarket ? selectedVmarket.base : newVmarket.base}
                      onValueChange={(value) =>
                        selectedVmarket
                          ? null
                          : setNewVmarket({ ...newVmarket, base: value })
                      }
                      disabled={!!selectedVmarket}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select base asset" />
                      </SelectTrigger>
                      <SelectContent>
                        {assets.map((asset) => (
                          <SelectItem key={asset.id} value={asset.code}>
                            {asset.code} - {asset.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Counter Asset
                    </label>
                    <Select
                      value={selectedVmarket ? selectedVmarket.counter : newVmarket.counter}
                      onValueChange={(value) =>
                        selectedVmarket
                          ? null
                          : setNewVmarket({ ...newVmarket, counter: value })
                      }
                      disabled={!!selectedVmarket}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select counter asset" />
                      </SelectTrigger>
                      <SelectContent>
                        {assets.map((asset) => (
                          <SelectItem key={asset.id} value={asset.code}>
                            {asset.code} - {asset.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* <div className="space-y-2">
                  <label className="text-sm font-medium text-gray-700">
                    Priority
                  </label>
                  <Input
                    type="number"
                    min="0"
                    placeholder="Enter priority (higher number = higher priority)"
                    value={selectedVmarket ? selectedVmarket.priority : newVmarket.priority}
                    onChange={(e) =>
                      selectedVmarket
                        ? setSelectedVmarket({
                            ...selectedVmarket,
                            priority: parseInt(e.target.value),
                          })
                        : setNewVmarket({
                            ...newVmarket,
                            priority: parseInt(e.target.value),
                          })
                    }
                  />
                </div> */}

                <div className="space-y-2">
                  <div className="flex justify-between items-center">
                    <label className="text-sm font-medium text-gray-700">
                      Route Hops
                    </label>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        if (selectedVmarket) {
                          setSelectedVmarket({
                            ...selectedVmarket,
                            route: {
                              ...selectedVmarket.route,
                              hops: [
                                ...selectedVmarket.route.hops,
                                { base: "", counter: "", exchange: Exchange.BINANCE, inverted: false }
                              ]
                            }
                          });
                        } else {
                          addHop();
                        }
                      }}
                      className="flex items-center"
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add Hop
                    </Button>
                  </div>
                  <div className="space-y-2">
                    {(selectedVmarket ? selectedVmarket.route.hops : newVmarket.hops).map((hop, index) => (
                      <div key={index} className="grid grid-cols-4 gap-2 items-center">
                        <Select
                          value={hop.base}
                          onValueChange={(value) => {
                            if (selectedVmarket) {
                              const updatedHops = [...selectedVmarket.route.hops];
                              updatedHops[index] = { ...updatedHops[index], base: value };
                              setSelectedVmarket({
                                ...selectedVmarket,
                                route: { ...selectedVmarket.route, hops: updatedHops }
                              });
                            } else {
                              updateHop(index, "base", value);
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select base asset" />
                          </SelectTrigger>
                          <SelectContent>
                            {assets.map((asset) => (
                              <SelectItem key={asset.id} value={asset.code}>
                                {asset.code}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={hop.counter}
                          onValueChange={(value) => {
                            if (selectedVmarket) {
                              const updatedHops = [...selectedVmarket.route.hops];
                              updatedHops[index] = { ...updatedHops[index], counter: value };
                              setSelectedVmarket({
                                ...selectedVmarket,
                                route: { ...selectedVmarket.route, hops: updatedHops }
                              });
                            } else {
                              updateHop(index, "counter", value);
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select counter asset" />
                          </SelectTrigger>
                          <SelectContent>
                            {assets.map((asset) => (
                              <SelectItem key={asset.id} value={asset.code}>
                                {asset.code}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Select
                          value={hop.exchange}
                          onValueChange={(value) => {
                            if (selectedVmarket) {
                              const updatedHops = [...selectedVmarket.route.hops];
                              updatedHops[index] = { ...updatedHops[index], exchange: value as Exchange };
                              setSelectedVmarket({
                                ...selectedVmarket,
                                route: { ...selectedVmarket.route, hops: updatedHops }
                              });
                            } else {
                              updateHop(index, "exchange", value as Exchange);
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select pricing venue" />
                          </SelectTrigger>
                          <SelectContent>
                            {Object.values(Exchange).map((exchange) => (
                              <SelectItem key={exchange} value={exchange}>
                                {exchange}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="destructive"
                          size="sm"
                          onClick={() => {
                            if (selectedVmarket) {
                              if (selectedVmarket.route.hops.length <= 1) {
                                toast({
                                  title: "Validation Error",
                                  description: "A virtual market must have at least one hop",
                                  variant: "error",
                                });
                                return;
                              }
                              const updatedHops = selectedVmarket.route.hops.filter((_, i) => i !== index);
                              setSelectedVmarket({
                                ...selectedVmarket,
                                route: { ...selectedVmarket.route, hops: updatedHops }
                              });
                            } else {
                              removeHop(index);
                            }
                          }}
                          className="flex items-center"
                        >
                          <Trash className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end space-x-2 pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setDialogOpen(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={
                      selectedVmarket ? handleEditVmarket : handleCreateVmarket
                    }
                    disabled={loading}
                    className="bg-gradient-to-r from-blue-600 to-blue-800 hover:from-blue-800 hover:to-blue-900"
                  >
                    {loading
                      ? selectedVmarket
                        ? "Updating..."
                        : "Creating..."
                      : selectedVmarket
                      ? "Update"
                      : "Create"}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </main>
    </div>
  );
}
